import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { InlineLoadingSpinner } from '@spins-gcp/shared-components-react';
import { useSetAtom } from 'jotai';
import type { SavedView } from '../../interfaces/SavedView';
import { toastAtom } from '../../atoms/toastAtom';
import { ToastVariant } from '../../constants/toast-variant';
import styles from '../power-bi/power-bi-page.module.scss';

interface PowerBiUpdateViewProps {
  activeView: SavedView | null;
  pendingName?: string | null;
  embedReady: boolean;
  onUpdateView: (viewId: string, viewName?: string) => Promise<void>;
  onUpdateSuccess?: () => void;
}

export function PowerBiUpdateView({
  activeView,
  pendingName,
  embedReady,
  onUpdateView,
  onUpdateSuccess,
}: PowerBiUpdateViewProps) {
  const [isSaving, setIsSaving] = useState(false);
  const setToast = useSetAtom(toastAtom);

  const handleSave = async () => {
    if (!activeView) return;
    const displayName = pendingName ?? activeView.viewName;
    setIsSaving(true);
    try {
      await onUpdateView(activeView.viewId, displayName);
      setToast({
        message: `"${displayName}" updated successfully.`,
        variant: ToastVariant.SUCCESS,
      });
      onUpdateSuccess?.();
    } catch (err) {
      setToast({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Failed to update view. Please try again.',
        variant: ToastVariant.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <button
      className={`${styles['toolbar-btn']} ${styles['toolbar-icon-btn']}`}
      onClick={handleSave}
      disabled={!activeView || !embedReady || isSaving}
      title={
        activeView
          ? `Update "${pendingName ?? activeView.viewName}" with current state`
          : 'Select a saved view first'
      }
      aria-label="Update view"
    >
      {isSaving ? (
        <InlineLoadingSpinner size={8} />
      ) : (
        <Save size={14} aria-hidden="true" />
      )}
    </button>
  );
}
